import { combineLatestDeepObject } from '@/utils/rxjs/generators/combine-latest-deep-object.generator';
import { extractFromRecord } from '@/utils/typing/branded.type';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import type { primitive } from '@/utils/typing/primitive.type';
import { Directive, EventEmitter, inject, Output } from '@angular/core';
import { combineLatest, type Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import type {
    BusinessIntelligenceDimensionCode,
    BusinessIntelligenceMeasureCode,
    BusinessIntelligenceMemberCode
} from '../../interfaces/BusinessIntelligence.interface';
import type { BusinessIntelligenceRepresentationComponentConfig } from './../../interfaces/BusinessIntelligenceRepresentation.interface';
import { BUSINESS_INTELLIGENCE_REPRESENTATION_CONFIG } from './business-intelligence-representation-config.token';
import type { BusinessIntelligenceRepresentationData } from './business-intelligence-representation-data.interface';
import { BUSINESS_INTELLIGENCE_REPRESENTATION_DATA } from './business-intelligence-representation-data.token';

@Directive()
export abstract class BusinessIntelligenceRepresentationComponent<
    RepresentationConfig extends BusinessIntelligenceRepresentationComponentConfig
> {
    protected _representationData$ = inject(BUSINESS_INTELLIGENCE_REPRESENTATION_DATA);
    protected _config$: Observable<RepresentationConfig> = inject(BUSINESS_INTELLIGENCE_REPRESENTATION_CONFIG);

    @Output() clickEvent: EventEmitter<any> = new EventEmitter();
    @Output() paginationRequested: EventEmitter<{ limit: number; pageCount: number } | null> = new EventEmitter();

    exportAsCsv$(): Observable<{ data: any[]; columns: { header: string; value: (line: any) => primitive }[] }> {
        return combineLatest([this._representationData$, this._config$]).pipe(
            filter(
                ([representationData, config]) =>
                    representationData && Object.keys(representationData.metadata).length > 0 && isNotNullish(config)
            ),
            switchMap(([representationData, config]) => {
                const members = this.getExportMembers(config).filter(member => isNotNullish(member));
                const data = representationData.data.map(dataPoint => {
                    return members.reduce((lineData, columnName, columnIndex) => {
                        const value =
                            extractFromRecord(dataPoint.dimensions, columnName as BusinessIntelligenceDimensionCode) ||
                            extractFromRecord(dataPoint.measures, columnName as BusinessIntelligenceMeasureCode);
                        (lineData as any)[columnIndex] =
                            representationData.metadata[columnName].valuesFormatting.exportData.formatAsExportData$(value);
                        return lineData;
                    }, {});
                });

                return combineLatestDeepObject({
                    data,
                    columns: members.map((column, index) => {
                        return {
                            header: this.getMemberNameForExport$(column, representationData),
                            value: (line: any) => line[index]
                        };
                    })
                });
            })
        );
    }

    protected getMemberName$(memberCode: string, representationData: BusinessIntelligenceRepresentationData): Observable<any> {
        return representationData.metadata[memberCode].getName$();
    }

    protected getMemberNameForExport$(memberCode: string, representationData: BusinessIntelligenceRepresentationData): Observable<any> {
        return combineLatest([
            representationData.metadata[memberCode].getName$(),
            representationData.metadata[memberCode].valuesFormatting.exportData.getRepeatedInformation$()
        ]).pipe(map(([name, repeatedValuesInformation]) => (repeatedValuesInformation ? `${name} (${repeatedValuesInformation})` : name)));
    }

    abstract getExportMembers(config: RepresentationConfig): BusinessIntelligenceMemberCode[];
}
